import { DeleteForever, StarSharp } from "@mui/icons-material"
import { Box, Button, CircularProgress, Grid2 as Grid, IconButton, Paper, TextField, Tooltip } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogEliminaFondoConSupporti from "../components/DialogEliminaFondoConSupporti"
import DialogForm from "../components/DialogForm"
import IconButtonDelete from "../components/IconButtonDelete"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import FondoForm from "../components/forms/FondoForm"
import { ACEnte, ACVocabolario, ACVocabolarioMulti } from "../components/forms/autocompletes"
import { useQsFilters } from "../hooks/filters"
import { useDownloadFondi, useFondiList, useFondoCreate, useFondoDelete } from "../hooks/fondi"
import useUserCanWrite, { useUserIsAdmin } from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Fondo } from "../types"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import filesaver from "file-saver"
import { useIsTablet } from "../hooks/tablet"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  genere: (params.getAll("genere") as unknown[]) ?? [],
  conservatore_originale: params.get("conservatore_originale") ?? "",
  conservatore_digitale: params.get("conservatore_digitale") ?? "",
  cronologia_da: params.get("cronologia_da") ?? "",
  cronologia_a: params.get("cronologia_a") ?? "",
  provenienza: params.get("provenienza") ?? "",
  tipo_acquisizione: params.get("tipo_acquisizione") ?? "",
  modo_acquisizione: params.get("modo_acquisizione") ?? "",
})

export default function Fondi() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const userCanWriteAndDelete = useUserCanWrite()
  const userIsAdmin = useUserIsAdmin()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()

  const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [deleteConSupportiModalState, deleteConSupportiModalActions] = useModalTrigger()

  const columns: GridColDef[] = useMemo(() => {
    const renderAzioni = (row: Fondo) => (
      <>
        <IconButtonDelete onClick={() => deleteModalActions.open(row)} disabled={!row.can_delete} />
        {userIsAdmin && !row.can_delete && (
          <Tooltip title={t("fondo.cta_elimina_con_supporti")}>
            <IconButton
              size="small"
              color="error"
              aria-label={t("fondo.cta_elimina_con_supporti")}
              onClick={() => deleteConSupportiModalActions.open(row)}
            >
              <DeleteForever />
            </IconButton>
          </Tooltip>
        )}
      </>
    )

    const cols: GridColDef[] = []

    cols.push({
      field: "segnatura",
      headerName: t("fondo.segnatura"),
      minWidth: 160,
      renderCell: (params) => (
        <LangLink to={`/fondi/${params.row.id}`} style={{ color: "black" }}>
          {params.value}
        </LangLink>
      ),
    })
    if (isMobile) {
      return cols
    }
    cols.push({
      field: "denominazione",
      headerName: t("fondo.denominazione"),
      flex: 1,
    })
    if (isTablet) {
      if (userCanWriteAndDelete) {
        cols.push({
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => renderAzioni(params.row),
        })
      }
      return cols
    }
    cols.push(
      {
        field: "provenienza_data",
        headerName: t("fondo.provenienza"),
        flex: 1,
        renderCell: (params) => {
          return i18n.language === "it"
            ? params.row.provenienza_data?.termine_it
            : params.row.provenienza_data?.termine_en
        },
      },
      {
        field: "tipo_acquisizione_data",
        headerName: t("fondo.tipo_acquisizione"),
        flex: 1,
        renderCell: (params) => {
          return i18n.language === "it"
            ? params.row.tipo_acquisizione_data?.termine_it
            : params.row.tipo_acquisizione_data?.termine_en
        },
      },
      {
        field: "numero_pellicole",
        headerName: t("fondo.numero_pellicole"),
        flex: 1,
      },
      {
        field: "durata_totale",
        headerName: t("fondo.durata_totale"),
        flex: 1,
      }
    )
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => renderAzioni(params.row),
      })
    }
    return cols
  }, [
    t,
    i18n.language,
    userCanWriteAndDelete,
    userIsAdmin,
    isMobile,
    isTablet,
    deleteModalActions,
    deleteConSupportiModalActions,
  ])

  const { data, isLoading } = useFondiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    genere: filters.genere as string[],
    conservatore_originale: filters.conservatore_originale,
    conservatore_digitale: filters.conservatore_digitale,
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
    provenienza: filters.provenienza,
    tipo_acquisizione: filters.tipo_acquisizione,
    modo_acquisizione: filters.modo_acquisizione,
  })

  const { mutateAsync: create } = useFondoCreate()
  const { mutateAsync: remove } = useFondoDelete()
  const { mutateAsync: downloadFondi, isPending: isPendingFondi } = useDownloadFondi({
    search: filters.search,
    ordering: filters.ordering,
    genere: filters.genere as string[],
    conservatore_originale: filters.conservatore_originale,
    conservatore_digitale: filters.conservatore_digitale,
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
    provenienza: filters.provenienza,
    tipo_acquisizione: filters.tipo_acquisizione,
    modo_acquisizione: filters.modo_acquisizione,
  })

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.genere.length > 0) count++
    if (filters.provenienza && filters.provenienza !== "") count++
    if (filters.tipo_acquisizione && filters.tipo_acquisizione !== "") count++
    if (filters.modo_acquisizione && filters.modo_acquisizione !== "") count++
    if (filters.conservatore_originale && filters.conservatore_originale !== "") count++
    if (filters.conservatore_digitale && filters.conservatore_digitale !== "") count++
    if (filters.cronologia_da !== "") count++
    if (filters.cronologia_a !== "") count++

    return count
  }, [
    filters.genere,
    filters.provenienza,
    filters.tipo_acquisizione,
    filters.conservatore_originale,
    filters.conservatore_digitale,
    filters.cronologia_a,
    filters.cronologia_da,
    filters.modo_acquisizione,
  ])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("fondo.titolo_lista")}
            breadcrumbs={[
              {
                label: t("fondo.titolo_lista"),
                iconType: StarSharp,
              },
            ]}
          />
          <Box>
            {userCanWriteAndDelete && !isMobile && (
              <Button
                sx={{
                  marginRight: 1,
                }}
                variant="contained"
                disabled={isPendingFondi}
                color="secondary"
                onClick={() => {
                  downloadFondi().then((blob) => {
                    filesaver.saveAs(blob, `fondi_${new Date().toISOString().split("T")[0]}.xlsx`)
                  })
                }}
              >
                {isPendingFondi ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                {t("fondo.download_excel")}
              </Button>
            )}
            {userCanWriteAndDelete && !isMobile && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  createModalActions.open()
                }}
              >
                {t("fondo.cta_aggiungi")}
              </Button>
            )}
          </Box>
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() =>
                setFilters({
                  search: "",
                  page: 1,
                  genere: [],
                  conservatore_originale: "",
                  cronologia_da: "",
                  cronologia_a: "",
                  conservatore_digitale: "",
                  provenienza: "",
                  tipo_acquisizione: "",
                  modo_acquisizione: "",
                })
              }
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("fondo.provenienza")}
                    vocabolario="fondo_provenienza"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.provenienza)}
                    onChange={(value) => {
                      setFilters({ ...filters, provenienza: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("fondo.tipo_acquisizione")}
                    vocabolario="fondo_tipo_acquisizione"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.tipo_acquisizione)}
                    onChange={(value) => {
                      setFilters({ ...filters, tipo_acquisizione: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("fondo.modalita_acquisizione")}
                    vocabolario="fondo_modo_acquisizione"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.modo_acquisizione)}
                    onChange={(value) => {
                      setFilters({ ...filters, modo_acquisizione: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolarioMulti
                    label={t("fondo.genere")}
                    vocabolario="fondo_genere"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.genere as number[]}
                    onChange={(value) => {
                      setFilters({ ...filters, genere: value, page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACEnte
                    label={t("fondo.conservatore_originale")}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.conservatore_originale)}
                    onChange={(value) => {
                      setFilters({ ...filters, conservatore_originale: value ? String(value) : "", page: 1 })
                    }}
                    initialCreate={(search) => ({ nome: search })}
                  />
                </Grid>
                <Grid size={3}>
                  <ACEnte
                    label={t("fondo.conservatore_digitale")}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.conservatore_digitale)}
                    onChange={(value) => {
                      setFilters({ ...filters, conservatore_digitale: value ? String(value) : "", page: 1 })
                    }}
                    initialCreate={(search) => ({ nome: search })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_da")}
                    type="number"
                    margin="dense"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={uiFilters.cronologia_da}
                    onChange={(e) => setFilters({ ...filters, cronologia_da: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_a")}
                    type="number"
                    margin="dense"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={uiFilters.cronologia_a}
                    onChange={(e) => setFilters({ ...filters, cronologia_a: e.target.value, page: 1 })}
                  />
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  // change label when no data
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Fondo>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("fondo.cta_aggiungi")}
        FormComponent={FondoForm}
        save={(values: Fondo) => {
          return create(values).then((response) => {
            createModalActions.close()
            showMessage(t("fondo.esito_crea_ok"))
            navigate(`/${i18n.language}/fondi/${response.id}`)
          })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("fondo.cta_elimina")}
        subtitle={
          <span>
            {t("fondo.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("fondo.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
      <DialogEliminaFondoConSupporti
        open={deleteConSupportiModalState.isOpen}
        handleClose={deleteConSupportiModalActions.close}
        fondo={deleteConSupportiModalState.value}
      />
    </Layout>
  )
}
